import React, { useEffect, useState } from 'react';

import i18next, { InitOptions, Resource } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import {
  getLanguage,
  lowercasePostProcessor,
  uppercasePostProcessor,
} from '../../utils';
import { overrideFontFamily } from '../../utils/overrideFontFamily';

overrideFontFamily();

type I18nWrapperProps = {
  children: React.ReactNode;
  errorScreen?: (e: Error) => React.ReactElement | null;
  loadingScreen?: React.ReactElement | null;
  translations: Resource;
  defaultNamespace?: string;
  /**
   * @deprecated We don't need to specify the namespace if we want to add new
   * resources after i18next is initialized. Just passing the new resources is
   * enough.
   */
  addNamespace?: string;
  locizeProjectId?: string;
};

const initializeTranslations = (
  currentLanguage: string,
  translations: Resource,
  defaultNamespace?: string
) => {
  const commonI18nOptions: Pick<
    InitOptions,
    'lng' | 'fallbackLng' | 'keySeparator' | 'interpolation' | 'defaultNS'
  > = {
    lng: currentLanguage,
    fallbackLng: 'en',
    keySeparator: false,
    interpolation: {
      escapeValue: false,
    },
    defaultNS: defaultNamespace,
  };

  if (i18next.isInitialized) {
    // if initialized, then dont change default, but add new name space with resources.
    Object.entries(translations).forEach(([language, resource]) => {
      Object.keys(resource).forEach((namespace) => {
        try {
          i18next.addResourceBundle(
            language,
            namespace,
            resource[namespace],
            true,
            true
          );
        } catch (error) {
          console.warn(
            `I18n resources for namespace "${namespace}" and language "${language}" couldn't load: ${error}`
          );
        }
      });
    });
    if (defaultNamespace) {
      i18next.setDefaultNamespace(defaultNamespace);
    }
    return Promise.resolve();
  }

  return i18next
    .use(initReactI18next)
    .use(lowercasePostProcessor)
    .use(uppercasePostProcessor)
    .init({
      ...commonI18nOptions,
      resources: translations,
    });
};

const I18nWrapper = ({
  children,
  defaultNamespace,
  errorScreen,
  loadingScreen,
  translations,
  locizeProjectId,
}: I18nWrapperProps): JSX.Element => {
  const language = getLanguage();

  const [didInitializeTranslations, setDidInitializeTranslations] =
    useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    if (!didInitializeTranslations) {
      initializeTranslations(language as string, translations, defaultNamespace)
        .then(() => {
          setDidInitializeTranslations(true);
        })
        .catch((e) => {
          setError(e);
        });
    }
  }, [
    defaultNamespace,
    didInitializeTranslations,
    language,
    translations,
    locizeProjectId,
  ]);

  if (error && errorScreen) {
    return errorScreen(error) as any;
  }

  if (!didInitializeTranslations || !i18next) {
    return <>{loadingScreen}</>;
  }

  return (
    <I18nContentWrapper loadingScreen={loadingScreen}>
      {children}
    </I18nContentWrapper>
  );
};

const I18nContentWrapper = ({
  children,
  loadingScreen,
}: Pick<I18nWrapperProps, 'children' | 'loadingScreen'>) => {
  const { ready } = useTranslation();

  if (!ready) {
    return <>{loadingScreen}</>;
  }

  return <>{children}</>;
};

export default I18nWrapper;
